import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import {
  Loader2, ArrowLeft, Copy, Camera, Video, Mic, MessageSquare, Trash2, Plus, ExternalLink, Bell, Anchor, Ship,
  SquareKanban, CheckCircle2, Plug, Tag, Archive, ArchiveRestore, X, RefreshCw, Monitor, Terminal, Flame, PlayCircle,
  Download, Maximize2, Smartphone, Pencil, ThumbsUp, Users, Map, Eye, EyeOff, Sparkles,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import ProjectSettings from "@/components/ProjectSettings";
import { SessionReplay } from "@/components/SessionReplay";
import { FeedbackTimeline } from "@/components/FeedbackTimeline";
import { ActivityTab } from "@/components/ActivityTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TYPE_META = {
  screenshot: { icon: Camera, label: "Screenshot" },
  screen_recording: { icon: Video, label: "Screen recording" },
  voice: { icon: Mic, label: "Voice note" },
  text: { icon: MessageSquare, label: "Text note" },
};
const PLATFORM_META = {
  ios: { label: "iOS", cls: "bg-ink text-cream" },
  android: { label: "Android", cls: "bg-grass text-white" },
  react_native: { label: "React Native", cls: "bg-teal text-ink" },
  flutter: { label: "Flutter", cls: "bg-teal text-ink" },
};
const parseViewportWidth = (vp) => {
  if (!vp) return null;
  const w = parseInt(String(vp).split(/[×x]/)[0], 10);
  return Number.isFinite(w) ? w : null;
};
// Show the real device/OS the submission came from. Native SDK → "<Platform> app";
// otherwise classify by the captured OS (Android, iOS, Windows, macOS, Linux),
// falling back to the recorded viewport width only when the OS is unknown.
const getDevice = (fb) => {
  const p = fb.platform;
  if (p && p !== "web") return { label: `${PLATFORM_META[p]?.label || "App"} app`, Icon: Smartphone, cls: "bg-ink text-cream" };
  const os = String(fb.env_metadata?.os || "");
  if (/android/i.test(os)) return { label: "Android", Icon: Smartphone, cls: "bg-grass text-white" };
  if (/ios|iphone|ipad|ipod/i.test(os)) return { label: "iOS", Icon: Smartphone, cls: "bg-ink text-cream" };
  if (/windows/i.test(os)) return { label: "Windows", Icon: Monitor, cls: "bg-teal text-ink" };
  if (/mac/i.test(os)) return { label: "macOS", Icon: Monitor, cls: "bg-teal text-ink" };
  if (/linux|cros|chrome os/i.test(os)) return { label: "Linux", Icon: Monitor, cls: "bg-teal text-ink" };
  const w = parseViewportWidth(fb.env_metadata?.viewport);
  if (w != null && w < 768) return { label: "Mobile", Icon: Smartphone, cls: "bg-orange text-white" };
  if (w != null) return { label: "Desktop", Icon: Monitor, cls: "bg-teal text-ink" };
  return { label: "Web", Icon: Monitor, cls: "bg-white text-ink" };
};
const STATUSES = [
  { v: "new", label: "New", dot: "bg-danger" },
  { v: "in_progress", label: "In progress", dot: "bg-sunny" },
  { v: "resolved", label: "Resolved", dot: "bg-grass" },
];

const NPS_FACES = { 1: "😠", 2: "🙁", 3: "😐", 4: "🙂", 5: "😍" };
const NPS_LABELS = { 1: "Very unhappy", 2: "Unhappy", 3: "Neutral", 4: "Happy", 5: "Very happy" };
const ROADMAP_COLS = [
  { v: "planned", label: "Planned", cls: "bg-white" },
  { v: "in_progress", label: "In progress", cls: "bg-sunny" },
  { v: "released", label: "Released", cls: "bg-teal" },
];

// Cohort targeting: convert the 3 comma-separated fields into a clean audience object (or null = everyone).
function parseAud(a) {
  const clean = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
  const out = {};
  ["plans", "roles", "segments"].forEach((k) => { const v = clean(a[k]); if (v.length) out[k] = v; });
  return Object.keys(out).length ? out : null;
}

function AudienceEditor({ value, onChange, idPrefix }) {
  return (
    <div className="border-2 border-ink/15 rounded-xl p-3" data-testid={`${idPrefix}-audience`}>
      <p className="text-[11px] font-800 uppercase tracking-wide text-ink/50 mb-1.5 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Audience (optional)</p>
      <p className="text-[11px] text-ink/50 font-600 mb-2">Show only to matching users. Leave blank for everyone. Comma-separated; matched against the <code className="font-mono">user</code> context you pass to the widget.</p>
      <div className="grid grid-cols-3 gap-2">
        {[["plans", "Plans"], ["roles", "Roles"], ["segments", "Segments"]].map(([k, label]) => (
          <div key={k}>
            <label className="text-[10px] font-800 uppercase tracking-wide text-ink/50 block mb-1">{label}</label>
            <input data-testid={`${idPrefix}-audience-${k}`} value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })}
              className="nb-input text-xs py-1.5" placeholder="any" />
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionVisibilityBar({ on, onToggle, title, projectKey, testid }) {
  return (
    <div className="nb-card p-4 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`${testid}-visibility-bar`}>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => onToggle(!on)} data-testid={`${testid}-visibility-toggle`} aria-pressed={on}
          className={`relative h-7 w-12 rounded-full border-2 border-ink transition-colors shrink-0 ${on ? "bg-grass" : "bg-white"}`}>
          <span className={`absolute top-[2px] h-5 w-5 rounded-full border-2 border-ink bg-white transition-all ${on ? "left-[22px]" : "left-[2px]"}`} />
        </button>
        <div>
          <p className="font-800 text-sm flex items-center gap-1.5">{on ? <Eye className="h-4 w-4 text-grass" /> : <EyeOff className="h-4 w-4 text-ink/40" />} {title}</p>
          <p className="text-[11px] text-ink/55 font-600">{on ? "Visible to testers in the floating widget." : "Hidden — testers won't see this panel."}</p>
        </div>
      </div>
      <Link to={`/preview/${projectKey}`} target="_blank" className="nb-btn bg-white px-3 py-2 text-xs shrink-0" data-testid={`${testid}-preview-link`}>
        <ExternalLink className="h-3.5 w-3.5" /> Preview
      </Link>
    </div>
  );
}


const TAG_COLORS = ["bg-teal", "bg-sunny", "bg-orange text-white", "bg-grass text-white", "bg-danger text-white", "bg-ink text-cream"];
function tagColor(t) {
  let h = 0;
  for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) >>> 0;
  return TAG_COLORS[h % TAG_COLORS.length];
}

function MediaViewer({ type, path }) {
  const [url, setUrl] = useState(null);
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    let revoked;
    apiClient.get(`/files/${path}`, { responseType: "blob" }).then((res) => {
      const u = URL.createObjectURL(res.data);
      revoked = u;
      setUrl(u);
    }).catch(() => setUrl(false));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [path]);

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); e.stopImmediatePropagation(); setZoom(false); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [zoom]);

  if (url === null) return <div className="h-48 flex items-center justify-center bg-sand border-2 border-ink rounded-2xl"><Loader2 className="h-5 w-5 animate-spin text-orange" /></div>;
  if (url === false) return <div className="h-24 flex items-center justify-center bg-sand border-2 border-ink rounded-2xl text-sm text-ink/60 font-600">Media unavailable</div>;

  const meta = TYPE_META[type] || TYPE_META.text;
  const baseName = (path.split("/").pop() || `asset`).split("?")[0];
  const fileName = `tugboat-${type}-${baseName}`;
  const canExpand = type === "screenshot" || type === "screen_recording";

  return (
    <div className="space-y-2.5">
      <div className="relative group">
        {type === "screenshot" && (
          <img src={url} alt="screenshot" onClick={() => setZoom(true)}
            className="w-full max-w-full rounded-2xl border-2 border-ink cursor-zoom-in" data-testid="media-screenshot" />
        )}
        {type === "screen_recording" && (
          <video src={url} controls className="w-full rounded-2xl border-2 border-ink bg-black" data-testid="media-video" />
        )}
        {type === "voice" && (
          <div className="bg-sand border-2 border-ink rounded-2xl p-3"><audio src={url} controls className="w-full" data-testid="media-audio" /></div>
        )}
        {canExpand && (
          <button onClick={() => setZoom(true)} data-testid="media-expand"
            className="absolute top-2 right-2 inline-flex items-center gap-1.5 bg-ink text-cream text-xs font-800 px-2.5 py-1.5 rounded-xl border-2 border-cream shadow-nb-sm hover:bg-orange transition-colors">
            <Maximize2 className="h-3.5 w-3.5" /> Expand
          </button>
        )}
      </div>
      <a href={url} download={fileName} data-testid="media-download"
        className="nb-btn bg-grass text-white w-full justify-center py-2.5 text-sm flex items-center gap-2">
        <Download className="h-4 w-4" /> Download {meta.label.toLowerCase()}
      </a>

      {zoom && createPortal(
        <div onClick={() => setZoom(false)} data-testid="media-lightbox"
          className="fixed inset-0 z-[2000] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8">
          <button onClick={() => setZoom(false)} data-testid="media-lightbox-close"
            className="absolute top-4 right-4 h-11 w-11 flex items-center justify-center bg-cream text-ink rounded-2xl border-2 border-ink shadow-nb hover:bg-danger hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
          {type === "screenshot" ? (
            <img src={url} alt="screenshot full" onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] max-w-[96vw] rounded-2xl border-2 border-cream shadow-nb-xl" data-testid="media-lightbox-image" />
          ) : (
            <video src={url} controls autoPlay onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] max-w-[96vw] rounded-2xl border-2 border-cream bg-black shadow-nb-xl" data-testid="media-lightbox-video" />
          )}
          <a href={url} download={fileName} onClick={(e) => e.stopPropagation()} data-testid="media-lightbox-download"
            className="absolute bottom-5 left-1/2 -translate-x-1/2 nb-btn bg-grass text-white px-5 py-2.5 text-sm flex items-center gap-2">
            <Download className="h-4 w-4" /> Download
          </a>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = ["owner", "admin", "member"].includes(user?.role);
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const submitRename = async () => {
    const nv = renameValue.trim();
    setRenaming(false);
    if (!nv || nv === project.name) return;
    setProject((p) => ({ ...p, name: nv }));
    try { await apiClient.patch(`/projects/${id}/rename`, { name: nv }); toast.success("Project renamed"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const [feedback, setFeedback] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [signalFilter, setSignalFilter] = useState("all");
  const [viewFilter, setViewFilter] = useState("inbox"); // inbox | archived | all
  const [active, setActive] = useState(null);
  const [changelog, setChangelog] = useState([]);
  const [clTitle, setClTitle] = useState("");
  const [clVersion, setClVersion] = useState("");
  const [clBody, setClBody] = useState("");
  const [clAud, setClAud] = useState({ plans: "", roles: "", segments: "" });
  const [roadmap, setRoadmap] = useState([]);
  const [rmTitle, setRmTitle] = useState("");
  const [rmBody, setRmBody] = useState("");
  const [rmStatus, setRmStatus] = useState("planned");
  const [rmAud, setRmAud] = useState({ plans: "", roles: "", segments: "" });
  const [jira, setJira] = useState(null);
  const [jiraForm, setJiraForm] = useState({ site_url: "", email: "", api_token: "", project_key: "", issue_type: "Task" });
  const [jiraSaving, setJiraSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [jiraSyncing, setJiraSyncing] = useState(false);
  const [replayEvents, setReplayEvents] = useState(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [tab, setTab] = useState("feedback");
  const [whatsnewOn, setWhatsnewOn] = useState(true);
  const [roadmapOn, setRoadmapOn] = useState(true);
  const [autoChangelogOn, setAutoChangelogOn] = useState(false);
  const toggleAutoChangelog = async (next) => {
    setAutoChangelogOn(next);
    if (next) setWhatsnewOn(true);
    try {
      await apiClient.put(`/projects/${id}/widget-sections`, { auto_changelog_on_resolve: next });
      toast.success(next ? "Resolved fixes will auto-post to What's New (panel shown to testers)" : "Auto-publish turned off");
    } catch (e) { setAutoChangelogOn(!next); toast.error("Could not save"); }
  };

  const toggleWidgetSection = async (key, next) => {
    const setter = key === "whatsnew" ? setWhatsnewOn : setRoadmapOn;
    setter(next);
    try {
      await apiClient.put(`/projects/${id}/widget-sections`, key === "whatsnew" ? { whatsnew_enabled: next } : { roadmap_enabled: next });
      toast.success(next ? "Panel enabled in the widget" : "Panel hidden from the widget");
    } catch (e) {
      setter(!next);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
    }
  };

  const loadReplay = useCallback(async (fb) => {
    setReplayLoading(true);
    try {
      const { data } = await apiClient.get(`/feedback/${fb.id}/replay`);
      setReplayEvents(data.events || []);
    } catch {
      toast.error("Could not load the session replay.");
    } finally {
      setReplayLoading(false);
    }
  }, []);

  const embed = `<script src="${BACKEND_URL}/widget.js"\n        data-project-key="${project?.project_key || ""}"></script>`;

  const PAGE = 50;
  const fetchPage = useCallback((skip) =>
    apiClient.get(`/projects/${id}/feedback`, { params: { status: statusFilter, subject: subjectFilter, signal: signalFilter, view: viewFilter, skip, limit: PAGE } })
      .then(({ data }) => data), [id, statusFilter, subjectFilter, signalFilter, viewFilter]);

  const loadFeedback = useCallback(() => {
    fetchPage(0).then((data) => {
      setFeedback(data.items || []);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    }).catch(() => {});
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    fetchPage(feedback.length).then((data) => {
      setFeedback((prev) => [...prev, ...(data.items || [])]);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    }).catch(() => {}).finally(() => setLoadingMore(false));
  }, [fetchPage, feedback.length]);

  const loadChangelog = useCallback(() => {
    apiClient.get(`/projects/${id}/changelog`).then(({ data }) => setChangelog(data)).catch(() => {});
  }, [id]);

  const loadRoadmap = useCallback(() => {
    apiClient.get(`/projects/${id}/roadmap`).then(({ data }) => setRoadmap(data)).catch(() => {});
  }, [id]);

  const loadJira = useCallback(() => {
    apiClient.get(`/projects/${id}/jira`).then(({ data }) => setJira(data)).catch(() => setJira({ connected: false }));
  }, [id]);

  useEffect(() => {
    apiClient.get(`/projects/${id}`).then(({ data }) => {
      setProject(data);
      setWhatsnewOn(data.whatsnew_enabled !== undefined ? data.whatsnew_enabled !== false : !(data.branding && data.branding.hide_whatsnew));
      setRoadmapOn(data.roadmap_enabled !== false);
      setAutoChangelogOn(!!data.auto_changelog_on_resolve);
    }).catch(() => navigate("/projects"));
    loadChangelog();
    loadRoadmap();
    loadJira();
  }, [id, navigate, loadChangelog, loadRoadmap, loadJira]);
  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingMore) loadMore();
    }, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, loadMore]);

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embed);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy automatically — select the snippet to copy manually.");
    }
  };

  const updateStatus = async (fb, status) => {
    await apiClient.put(`/feedback/${fb.id}`, { status });
    toast.success("Status updated");
    setFeedback((prev) => prev.map((x) => (x.id === fb.id ? { ...x, status } : x)));
    setActive((a) => (a && a.id === fb.id ? { ...a, status } : a));
  };

  const deleteFeedback = async (fb) => {
    await apiClient.delete(`/feedback/${fb.id}`);
    toast.success("Feedback deleted");
    setActive(null);
    setFeedback((prev) => prev.filter((x) => x.id !== fb.id));
    setTotal((t) => Math.max(0, t - 1));
  };

  const updateTags = async (fb, tags) => {
    await apiClient.put(`/feedback/${fb.id}`, { tags });
    setActive((a) => (a && a.id === fb.id ? { ...a, tags } : a));
    setFeedback((prev) => prev.map((x) => (x.id === fb.id ? { ...x, tags } : x)));
  };

  const addTag = async (fb, raw) => {
    const t = (raw || "").trim().toLowerCase().slice(0, 24);
    if (!t || (fb.tags || []).includes(t)) { setTagInput(""); return; }
    await updateTags(fb, [...(fb.tags || []), t]);
    setTagInput("");
  };

  const removeTag = async (fb, t) => {
    await updateTags(fb, (fb.tags || []).filter((x) => x !== t));
  };

  const archiveFeedback = async (fb, archived) => {
    await apiClient.put(`/feedback/${fb.id}`, { archived });
    toast.success(archived ? "Archived" : "Restored to inbox");
    setActive(null);
    if (viewFilter === "all") {
      setFeedback((prev) => prev.map((x) => (x.id === fb.id ? { ...x, archived } : x)));
    } else {
      setFeedback((prev) => prev.filter((x) => x.id !== fb.id));
      setTotal((t) => Math.max(0, t - 1));
    }
  };

  const syncJira = async () => {
    setJiraSyncing(true);
    try {
      const { data } = await apiClient.post(`/projects/${id}/jira/sync`);
      toast.success(data.resolved > 0
        ? `Auto-resolved ${data.resolved} item${data.resolved === 1 ? "" : "s"} from closed Jira issues`
        : "Synced — no closed Jira issues yet");
      loadFeedback();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setJiraSyncing(false);
    }
  };

  const postChangelog = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/projects/${id}/changelog`, { title: clTitle, version: clVersion, body: clBody, audience: parseAud(clAud) });
      toast.success("Published to the widget! 📣");
      setClTitle(""); setClVersion(""); setClBody(""); setClAud({ plans: "", roles: "", segments: "" });
      loadChangelog();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteChangelog = async (clId) => {
    await apiClient.delete(`/changelog/${clId}`);
    loadChangelog();
  };

  const postRoadmap = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/projects/${id}/roadmap`, { title: rmTitle, body: rmBody, status: rmStatus, audience: parseAud(rmAud) });
      toast.success("Added to the roadmap! 🗺️");
      setRmTitle(""); setRmBody(""); setRmStatus("planned"); setRmAud({ plans: "", roles: "", segments: "" });
      loadRoadmap();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changeRoadmapStatus = async (item, status) => {
    setRoadmap((prev) => prev.map((r) => (r.id === item.id ? { ...r, status } : r)));
    try {
      await apiClient.put(`/roadmap/${item.id}`, { status });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      loadRoadmap();
    }
  };

  const deleteRoadmap = async (rmId) => {
    await apiClient.delete(`/roadmap/${rmId}`);
    loadRoadmap();
  };

  const connectJira = async (e) => {
    e.preventDefault();
    setJiraSaving(true);
    try {
      const { data } = await apiClient.post(`/projects/${id}/jira`, jiraForm);
      setJira({ connected: true, ...data, email: jiraForm.email });
      toast.success("Jira connected! 🔗");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setJiraSaving(false);
    }
  };

  const disconnectJira = async () => {
    await apiClient.delete(`/projects/${id}/jira`);
    setJira({ connected: false });
    setJiraForm({ site_url: "", email: "", api_token: "", project_key: "", issue_type: "Task" });
    toast.success("Jira disconnected");
  };

  const pushToJira = async (fb) => {
    setPushing(true);
    try {
      const { data } = await apiClient.post(`/feedback/${fb.id}/jira`);
      toast.success(`Sent to Jira: ${data.issue_key}`);
      setActive((a) => (a && a.id === fb.id ? { ...a, jira_issue_key: data.issue_key, jira_issue_url: data.issue_url } : a));
      loadFeedback();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPushing(false);
    }
  };

  if (!project) return <div className="flex items-center justify-center h-screen"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>;

  return (
    <div>
      <div className="h-20 flex items-center gap-4 px-4 sm:px-8">
        <Link to="/projects" className="nb-btn bg-white h-10 w-10 p-0 shrink-0" data-testid="back-to-projects"><ArrowLeft className="h-5 w-5" /></Link>
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-11 w-11 bg-teal border-2 border-ink rounded-2xl flex items-center justify-center shadow-nb-sm shrink-0"><Ship className="h-5 w-5" /></div>
          <div className="min-w-0">
            {renaming ? (
              <input autoFocus data-testid="project-rename-input" value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitRename(); if (e.key === "Escape") setRenaming(false); }}
                onBlur={submitRename} maxLength={80}
                className="nb-input font-display text-2xl font-700 tracking-tight py-1 leading-none" />
            ) : (
              <h1 className="font-display text-2xl font-700 tracking-tight leading-none truncate flex items-center gap-2">
                {project.name}
                {canEdit && (
                  <button data-testid="project-rename-btn" title="Rename project"
                    onClick={() => { setRenameValue(project.name); setRenaming(true); }}
                    className="p-1 rounded-lg text-ink/30 hover:text-orange hover:bg-sand transition-colors shrink-0"><Pencil className="h-4 w-4" /></button>
                )}
              </h1>
            )}
            {project.client_name && <span className="label-overline">{project.client_name}</span>}
          </div>
        </div>
        <Link to={`/preview/${project.project_key}`} target="_blank" data-testid="header-preview-widget"
          className="nb-btn bg-teal px-4 py-2.5 text-sm ml-auto shrink-0">
          <ExternalLink className="h-4 w-4" /> <span className="hidden sm:inline">Preview widget</span>
        </Link>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 pt-2">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border-2 border-ink rounded-full p-1 shadow-nb-sm h-auto gap-1 w-full max-w-full overflow-x-auto no-scrollbar flex-nowrap justify-start">
            <TabsTrigger value="feedback" data-testid="tab-feedback" className="rounded-full px-5 py-2 font-700 shrink-0 data-[state=active]:bg-orange data-[state=active]:text-white">Feedback</TabsTrigger>
            <TabsTrigger value="changelog" data-testid="tab-changelog" className="rounded-full px-5 py-2 font-700 shrink-0 data-[state=active]:bg-orange data-[state=active]:text-white">Changelog</TabsTrigger>
            <TabsTrigger value="roadmap" data-testid="tab-roadmap" className="rounded-full px-5 py-2 font-700 shrink-0 data-[state=active]:bg-orange data-[state=active]:text-white">Roadmap</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity" className="rounded-full px-5 py-2 font-700 shrink-0 data-[state=active]:bg-orange data-[state=active]:text-white">Activity</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-full px-5 py-2 font-700 shrink-0 data-[state=active]:bg-orange data-[state=active]:text-white">Settings</TabsTrigger>
          </TabsList>

          {/* FEEDBACK */}
          <TabsContent value="feedback" className="mt-6">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Select value={viewFilter} onValueChange={setViewFilter}>
                <SelectTrigger className="w-40 border-2 border-ink rounded-xl font-700 bg-white shadow-nb-sm" data-testid="view-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="border-2 border-ink rounded-2xl">
                  <SelectItem value="inbox">Inbox</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                  <SelectItem value="all">All (incl. archived)</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44 border-2 border-ink rounded-xl font-700 bg-white shadow-nb-sm" data-testid="status-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="border-2 border-ink rounded-2xl">
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-sm text-ink/60 font-700" data-testid="feedback-count">{total} item{total === 1 ? "" : "s"}</span>
              <Select value={signalFilter} onValueChange={setSignalFilter}>
                <SelectTrigger className="w-44 border-2 border-ink rounded-xl font-700 bg-white shadow-nb-sm" data-testid="signal-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="border-2 border-ink rounded-2xl">
                  <SelectItem value="all">All signals</SelectItem>
                  <SelectItem value="errors"><span className="flex items-center gap-1.5"><Flame className="h-3.5 w-3.5 text-danger" /> Has errors</span></SelectItem>
                  <SelectItem value="replays"><span className="flex items-center gap-1.5"><PlayCircle className="h-3.5 w-3.5 text-teal" /> Has replay</span></SelectItem>
                </SelectContent>
              </Select>
              {project.subjects?.length > 0 && (
                <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                  <SelectTrigger className="w-44 border-2 border-ink rounded-xl font-700 bg-white shadow-nb-sm ml-auto" data-testid="subject-filter"><SelectValue placeholder="Subject" /></SelectTrigger>
                  <SelectContent className="border-2 border-ink rounded-2xl">
                    <SelectItem value="all">All subjects</SelectItem>
                    {project.subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            {feedback.length === 0 ? (
              <div className="nb-card p-16 text-center">
                <Anchor className="h-12 w-12 text-ink/20 mx-auto mb-4" />
                <p className="text-ink/60 font-600 mb-5">No feedback yet. Embed the widget and your testers' captures will wash up here.</p>
                <button onClick={() => setTab("settings")} data-testid="onboarding-install-nudge"
                  className="nb-btn bg-orange text-white px-5 py-2.5 text-sm inline-flex">
                  <Copy className="h-4 w-4" /> Get the embed snippet
                </button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="feedback-list">
                {feedback.map((fb) => {
                  const M = TYPE_META[fb.type] || TYPE_META.text;
                  const st = STATUSES.find((s) => s.v === fb.status) || STATUSES[0];
                  const hasErrors = (fb.console_logs || []).some((l) => l.level === "error") || (fb.network_logs || []).length > 0;
                  const dev = getDevice(fb);
                  return (
                    <div key={fb.id} onClick={() => setActive(fb)} data-testid={`feedback-row-${fb.id}`}
                      role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter") setActive(fb); }}
                      className="group flex flex-col md:flex-row md:items-start gap-4 p-4 bg-white border-2 border-ink rounded-2xl shadow-nb-sm hover:-translate-y-0.5 hover:shadow-nb transition-all cursor-pointer">

                      {/* Left: status dot + type icon */}
                      <div className="flex items-center gap-3 shrink-0 md:pt-0.5">
                        <span className={`h-2.5 w-2.5 rounded-full border-2 border-ink shrink-0 ${st.dot}`} title={st.label} />
                        <span className="h-9 w-9 bg-ink/5 rounded-lg flex items-center justify-center shrink-0 text-ink/60"><M.icon className="h-4 w-4" /></span>
                      </div>

                      {/* Main: title/description + quiet meta */}
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-700 text-ink leading-snug line-clamp-2">
                          {fb.number ? <span className="font-800 text-ink/40 mr-1.5">#{fb.number}</span> : null}
                          {fb.text_note || M.label}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs">
                          <span className="font-700 text-ink/70">{fb.submitter_name || "Anonymous"}</span>
                          {fb.page_url && <span className="text-ink/40 truncate max-w-[160px] md:max-w-[240px]" title={fb.page_url}>{fb.page_url.replace(/^https?:\/\//, "")}</span>}
                          <span className="inline-flex items-center gap-1 text-ink/45 font-600" data-testid={`feedback-row-device-${fb.id}`} title={`Captured on ${dev.label}`}>
                            <dev.Icon className="h-3 w-3" /> {dev.label}
                          </span>
                          {fb.subject && <span className="font-700 text-ink/60 bg-ink/5 px-2 py-0.5 rounded">{fb.subject}</span>}
                          {fb.tags?.slice(0, 3).map((t) => (
                            <span key={t} className="font-700 text-orange bg-orange/10 px-2 py-0.5 rounded" data-testid={`feedback-row-tags-${fb.id}`}>{t}</span>
                          ))}
                          {hasErrors && (
                            <span className="inline-flex items-center gap-1 font-800 text-danger bg-danger/10 px-2 py-0.5 rounded" data-testid={`feedback-row-errors-${fb.id}`} title="Includes console errors or failed network requests">
                              <Flame className="h-3 w-3" /> Errors
                            </span>
                          )}
                          {fb.audio_path && (
                            <span className="inline-flex items-center gap-1 font-700 text-ink/60 bg-ink/5 px-2 py-0.5 rounded" data-testid={`feedback-row-voice-${fb.id}`} title="Includes a voice note">
                              <Mic className="h-3 w-3" /> Voice
                            </span>
                          )}
                          {fb.rating > 0 && <span className="text-sm" data-testid={`feedback-row-rating-${fb.id}`} title={NPS_LABELS[fb.rating]}>{NPS_FACES[fb.rating]}</span>}
                          {fb.archived && <span className="inline-flex items-center gap-1 font-700 text-ink/50"><Archive className="h-3 w-3" /> Archived</span>}
                        </div>
                      </div>

                      {/* Right: status + actions */}
                      <div className="flex items-center gap-2 shrink-0 justify-end" onClick={(e) => e.stopPropagation()}>
                        <Select value={fb.status || "new"} onValueChange={(v) => updateStatus(fb, v)}>
                          <SelectTrigger data-testid={`feedback-row-status-${fb.id}`}
                            className="w-[128px] h-9 border-2 border-ink rounded-lg font-700 text-xs bg-white shadow-nb-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="border-2 border-ink rounded-2xl">
                            {STATUSES.map((s) => (
                              <SelectItem key={s.v} value={s.v}>
                                <span className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full border-2 border-ink ${s.dot}`} /> {s.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {canEdit && (
                          <span className="flex items-center gap-0.5 shrink-0 md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                            <button onClick={() => archiveFeedback(fb, !fb.archived)} data-testid={`feedback-row-archive-${fb.id}`}
                              title={fb.archived ? "Restore to inbox" : "Archive"}
                              className="h-8 w-8 flex items-center justify-center rounded-lg text-ink/40 hover:text-ink hover:bg-ink/5 transition-colors">
                              {fb.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                            </button>
                            <button onClick={() => setConfirmDel(fb)} data-testid={`feedback-row-delete-${fb.id}`} title="Delete"
                              className="h-8 w-8 flex items-center justify-center rounded-lg text-ink/40 hover:text-white hover:bg-danger transition-colors">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {hasMore && (
              <div ref={sentinelRef} className="flex justify-center py-6">
                <button onClick={loadMore} disabled={loadingMore} data-testid="feedback-load-more"
                  className="nb-btn bg-white border-2 border-ink px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60">
                  {loadingMore ? <><Loader2 className="h-4 w-4 animate-spin" /> Loading…</> : <>Load more · {total - feedback.length} left</>}
                </button>
              </div>
            )}
          </TabsContent>

          {/* CHANGELOG */}
          <TabsContent value="changelog" className="mt-6 max-w-2xl">
            <SectionVisibilityBar on={whatsnewOn} onToggle={(n) => toggleWidgetSection("whatsnew", n)} title={"Show \u201cWhat's new\u201d in the widget"} projectKey={project.project_key} testid="whatsnew" />
            <div className="nb-card p-4 mb-4 flex items-center justify-between gap-3" data-testid="auto-changelog-bar">
              <div>
                <p className="font-800 text-sm flex items-center gap-2"><Sparkles className="h-4 w-4 text-orange" /> Auto-publish resolved fixes</p>
                <p className="text-xs text-ink/60 font-600 mt-0.5">When you mark a submission <b>Resolved</b>, post it here automatically (uses your public reply if there is one). Testers see it in the widget's What's New.</p>
              </div>
              <button type="button" onClick={() => toggleAutoChangelog(!autoChangelogOn)} data-testid="auto-changelog-toggle" aria-pressed={autoChangelogOn}
                className={`relative h-7 w-12 rounded-full border-2 border-ink transition-colors shrink-0 ${autoChangelogOn ? "bg-grass" : "bg-white"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full border-2 border-ink bg-white transition-all ${autoChangelogOn ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>
            <div className="nb-card p-6 mb-4">
              <h3 className="font-display font-700 text-xl mb-1 flex items-center gap-2"><Bell className="h-5 w-5 text-orange" /> Publish update</h3>
              <p className="text-sm text-ink/60 mb-4 font-600">Testers see this inside the widget's "What's New" panel.</p>
              <form onSubmit={postChangelog} className="space-y-3">
                <div className="flex gap-3">
                  <input data-testid="changelog-title-input" required value={clTitle} onChange={(e) => setClTitle(e.target.value)} placeholder="Fixed transfer timeout"
                    className="nb-input flex-1" />
                  <input data-testid="changelog-version-input" value={clVersion} onChange={(e) => setClVersion(e.target.value)} placeholder="v1.4.2"
                    className="nb-input w-28 font-mono" />
                </div>
                <textarea data-testid="changelog-body-input" value={clBody} onChange={(e) => setClBody(e.target.value)} rows={2} placeholder="What changed?"
                  className="nb-input" />
                <AudienceEditor value={clAud} onChange={setClAud} idPrefix="changelog" />
                <button type="submit" data-testid="publish-changelog-button"
                  className="nb-btn bg-orange text-white px-5 py-2.5 text-sm"><Plus className="h-4 w-4" /> Publish</button>
              </form>
            </div>

            {changelog.length > 0 && (
              <div className="nb-card p-6 space-y-4" data-testid="changelog-list">
                {changelog.map((c) => (
                  <div key={c.id} className="border-l-4 border-orange pl-4 flex items-start justify-between gap-4">
                    <div>
                      {c.version && <span className="font-mono text-xs text-orange font-600">{c.version}</span>}
                      <h4 className="font-700 text-sm">{c.title}</h4>
                      {c.body && <p className="text-sm text-ink/60 mt-0.5 font-600">{c.body}</p>}
                      {c.audience && <span className="inline-flex items-center gap-1 text-[10px] font-800 mt-1 px-2 py-0.5 rounded-full border-2 border-ink bg-sand text-ink/70"><Users className="h-3 w-3" /> Targeted</span>}
                    </div>
                    <button onClick={() => deleteChangelog(c.id)} data-testid={`delete-changelog-${c.id}`} className="text-ink/40 hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ROADMAP */}
          <TabsContent value="roadmap" className="mt-6 max-w-2xl">
            <SectionVisibilityBar on={roadmapOn} onToggle={(n) => toggleWidgetSection("roadmap", n)} title="Show Roadmap in the widget" projectKey={project.project_key} testid="roadmap" />
            <div className="nb-card p-6 mb-4">
              <h3 className="font-display font-700 text-xl mb-1 flex items-center gap-2"><Map className="h-5 w-5 text-orange" /> Add roadmap item</h3>
              <p className="text-sm text-ink/60 mb-4 font-600">Testers see this inside the widget's "Roadmap" panel and can upvote what matters most.</p>
              <form onSubmit={postRoadmap} className="space-y-3">
                <input data-testid="roadmap-title-input" required value={rmTitle} onChange={(e) => setRmTitle(e.target.value)} placeholder="Dark mode" className="nb-input" />
                <textarea data-testid="roadmap-body-input" value={rmBody} onChange={(e) => setRmBody(e.target.value)} rows={2} placeholder="Describe what's coming (optional)" className="nb-input" />
                <div>
                  <label className="label-overline block mb-1.5">Status</label>
                  <Select value={rmStatus} onValueChange={setRmStatus}>
                    <SelectTrigger className="w-48 border-2 border-ink rounded-xl font-700 bg-white" data-testid="roadmap-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROADMAP_COLS.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <AudienceEditor value={rmAud} onChange={setRmAud} idPrefix="roadmap" />
                <button type="submit" data-testid="add-roadmap-button" className="nb-btn bg-orange text-white px-5 py-2.5 text-sm"><Plus className="h-4 w-4" /> Add to roadmap</button>
              </form>
            </div>

            {roadmap.length === 0 ? (
              <div className="nb-card p-8 text-center text-sm text-ink/50 font-600" data-testid="roadmap-empty">No roadmap items yet — add your first above.</div>
            ) : (
              <div className="grid sm:grid-cols-3 gap-3" data-testid="roadmap-board">
                {ROADMAP_COLS.map((col) => (
                  <div key={col.v} className="space-y-2">
                    <div className={`text-xs font-800 uppercase tracking-wide px-3 py-1.5 rounded-full border-2 border-ink ${col.cls}`}>{col.label}</div>
                    {roadmap.filter((r) => r.status === col.v).map((r) => (
                      <div key={r.id} className="nb-card p-3" data-testid={`roadmap-item-${r.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-1 text-sm font-800 shrink-0"><ThumbsUp className="h-3.5 w-3.5 text-orange" /> {r.votes || 0}</div>
                          <button onClick={() => deleteRoadmap(r.id)} data-testid={`delete-roadmap-${r.id}`} className="text-ink/40 hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                        </div>
                        <h4 className="font-700 text-sm mt-1">{r.title}</h4>
                        {r.body && <p className="text-xs text-ink/60 mt-0.5 font-600">{r.body}</p>}
                        {r.audience && <span className="inline-flex items-center gap-1 text-[10px] font-800 mt-1.5 px-2 py-0.5 rounded-full border-2 border-ink bg-sand text-ink/70"><Users className="h-3 w-3" /> Targeted</span>}
                        <div className="mt-2">
                          <Select value={r.status} onValueChange={(v) => changeRoadmapStatus(r, v)}>
                            <SelectTrigger className="h-8 text-xs border-2 border-ink rounded-lg font-700 bg-white" data-testid={`roadmap-move-${r.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ROADMAP_COLS.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SETTINGS (Install + Project settings + Integrations) */}
          <TabsContent value="activity" className="mt-6">
            <ActivityTab projectId={id} />
          </TabsContent>

          <TabsContent value="settings" className="mt-6 space-y-4">
            <div className="nb-card p-6 max-w-2xl">
              <h3 className="font-display font-700 text-xl mb-1">Embed snippet</h3>
              <p className="text-sm text-ink/60 mb-4 font-600">Paste this right before the closing <code className="font-mono text-xs bg-sand border border-ink/20 px-1 py-0.5 rounded">&lt;/body&gt;</code> tag of your beta app.</p>
              <div className="relative">
                <pre className="bg-ink text-sunny text-xs p-4 rounded-2xl overflow-x-auto font-mono border-2 border-ink" data-testid="embed-code">{embed}</pre>
                <button onClick={copyEmbed} data-testid="copy-embed-button"
                  className="absolute top-3 right-3 bg-orange text-white border-2 border-white/30 p-2 rounded-xl hover:bg-orange-dark transition-colors"><Copy className="h-3.5 w-3.5" /></button>
              </div>
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <span className="label-overline">Project key</span>
                <code className="font-mono text-xs bg-sunny border-2 border-ink px-2 py-1 rounded-lg" data-testid="project-key">{project.project_key}</code>
              </div>
              <Link to={`/preview/${project.project_key}`} target="_blank" data-testid="open-preview-link"
                className="nb-btn mt-6 bg-teal px-5 py-2.5 text-sm">
                <ExternalLink className="h-4 w-4" /> Open live preview
              </Link>
            </div>

            <ProjectSettings
              projectId={id}
              plan={project.plan}
              brandingEnabled={project.features?.branding}
              initialSubjects={project.subjects || []}
              initialBranding={project.branding || {}}
              initialMode={project.widget_mode || "floating"}
              initialPosition={project.button_position || "bottom-right"}
              initialHotkey={project.hotkey || "mod+shift+k"}
              initialHotkeyEnabled={project.hotkey_enabled !== false}
              initialNotify={{ recipients: project.notify_recipients || "owner", user_ids: project.notify_user_ids || [] }}
              initialPII={project.pii_redaction !== false}
              initialCountdown={project.capture_countdown !== false}
              whatsnewOn={whatsnewOn}
              roadmapOn={roadmapOn}
            />

            <div className="nb-card p-6 max-w-2xl">
              <h3 className="font-display font-700 text-xl mb-1 flex items-center gap-2"><SquareKanban className="h-5 w-5 text-orange" /> Jira Cloud</h3>
              <p className="text-sm text-ink/60 mb-5 font-600">Connect this project's Jira so feedback — screenshots, recordings & voice included — can be pushed straight into a ticket.</p>

              {jira === null ? (
                <div className="flex items-center gap-2 text-ink/50 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : jira.connected ? (
                <div data-testid="jira-connected">
                  <div className="flex items-center gap-2 bg-grass/15 border-2 border-grass rounded-2xl px-4 py-3 mb-4">
                    <CheckCircle2 className="h-5 w-5 text-grass" />
                    <div className="text-sm font-700">Connected to <span className="font-mono">{jira.site_url}</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm mb-5">
                    <div><p className="label-overline mb-1">Project key</p><code className="font-mono bg-sunny border-2 border-ink px-2 py-1 rounded-lg text-xs">{jira.project_key}</code></div>
                    <div><p className="label-overline mb-1">Issue type</p><span className="font-700">{jira.issue_type}</span></div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={syncJira} disabled={jiraSyncing} data-testid="jira-sync-button" className="nb-btn bg-teal px-4 py-2.5 text-sm disabled:opacity-60">
                      {jiraSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4" /> Sync from Jira</>}
                    </button>
                    <button onClick={disconnectJira} data-testid="jira-disconnect" className="nb-btn bg-white px-4 py-2.5 text-sm hover:bg-danger hover:text-white">
                      Disconnect Jira
                    </button>
                  </div>
                  <p className="text-xs text-ink/55 font-600 mt-3">Tugboat auto-resolves feedback when its linked Jira issue is marked done (checked every few minutes, or on demand here).</p>
                </div>
              ) : (
                <form onSubmit={connectJira} className="space-y-3" data-testid="jira-form">
                  <div>
                    <label className="label-overline block mb-1.5">Jira site URL</label>
                    <input data-testid="jira-site-input" required value={jiraForm.site_url} onChange={(e) => setJiraForm({ ...jiraForm, site_url: e.target.value })}
                      className="nb-input" placeholder="https://yourcompany.atlassian.net" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label-overline block mb-1.5">Atlassian email</label>
                      <input data-testid="jira-email-input" type="email" required value={jiraForm.email} onChange={(e) => setJiraForm({ ...jiraForm, email: e.target.value })}
                        className="nb-input" placeholder="you@company.com" />
                    </div>
                    <div>
                      <label className="label-overline block mb-1.5">Project key</label>
                      <input data-testid="jira-projectkey-input" required value={jiraForm.project_key} onChange={(e) => setJiraForm({ ...jiraForm, project_key: e.target.value })}
                        className="nb-input font-mono" placeholder="FB" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label-overline block mb-1.5">API token</label>
                      <input data-testid="jira-token-input" type="password" required value={jiraForm.api_token} onChange={(e) => setJiraForm({ ...jiraForm, api_token: e.target.value })}
                        className="nb-input" placeholder="Atlassian API token" />
                    </div>
                    <div>
                      <label className="label-overline block mb-1.5">Issue type</label>
                      <input data-testid="jira-issuetype-input" value={jiraForm.issue_type} onChange={(e) => setJiraForm({ ...jiraForm, issue_type: e.target.value })}
                        className="nb-input" placeholder="Task" />
                    </div>
                  </div>
                  <p className="text-xs text-ink/50 font-600">
                    Create a token at <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noreferrer" className="text-orange font-800 hover:underline">id.atlassian.com → API tokens</a>. It's encrypted before storage.
                  </p>
                  <button type="submit" data-testid="jira-connect-button" disabled={jiraSaving} className="nb-btn bg-orange text-white px-5 py-2.5 text-sm disabled:opacity-60">
                    {jiraSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plug className="h-4 w-4" /> Connect Jira</>}
                  </button>
                </form>
              )}
            </div>

            <div className="nb-card p-6 mt-4 max-w-2xl">
              <h3 className="font-display font-700 text-xl mb-1">More integrations</h3>
              <p className="text-sm text-ink/60 mb-5 font-600">Want feedback flowing into another tool? These are on the way — tell us which to prioritize.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="integration-directory">
                {["Linear", "GitHub Issues", "Asana", "Trello", "ClickUp", "Slack"].map((name) => (
                  <div key={name} className="flex flex-col items-center justify-center gap-2 p-4 border-2 border-ink/30 border-dashed rounded-2xl bg-cream text-center">
                    <SquareKanban className="h-5 w-5 text-ink/50" />
                    <span className="font-800 text-sm text-ink/70">{name}</span>
                    <span className="text-[10px] font-800 uppercase tracking-wide px-2 py-0.5 rounded-full border-2 border-ink/30 bg-white text-ink/50">Coming soon</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Feedback detail dialog */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setReplayEvents(null); } }}>
        <DialogContent className="border-2 border-ink rounded-3xl shadow-nb-xl max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden min-w-0" data-testid="feedback-detail-dialog">
          {active && (() => {
            const M = TYPE_META[active.type] || TYPE_META.text;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl font-700 tracking-tight flex items-center gap-2 flex-wrap"><M.icon className="h-5 w-5 text-orange" /> {M.label}{active.number ? <span className="text-ink/40 font-800" data-testid="feedback-number">#{active.number}</span> : null}
                    {(() => { const d = getDevice(active); return (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-800 uppercase px-2 py-1 rounded-full border-2 border-ink ${d.cls}`} data-testid="feedback-device" title={`Captured on ${d.label}${active.env_metadata?.viewport ? ` · ${active.env_metadata.viewport}` : ""}`}>
                        <d.Icon className="h-3 w-3" /> {d.label}
                      </span>
                    ); })()}
                  </DialogTitle>
                  <DialogDescription className="text-ink/60">Feedback captured from your beta tester.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-1 min-w-0">
                  {active.subject && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-800 px-3 py-1 rounded-full border-2 border-ink bg-sunny" data-testid="feedback-subject"><Tag className="h-3 w-3" /> {active.subject}</span>
                  )}
                  {active.rating > 0 && (
                    <div className="flex items-center gap-2" data-testid="feedback-rating">
                      <span className="text-2xl leading-none">{NPS_FACES[active.rating]}</span>
                      <span className="text-sm font-700 text-ink/70">{NPS_LABELS[active.rating]}</span>
                    </div>
                  )}
                  {active.media_path && <MediaViewer type={active.type} path={active.media_path} />}
                  {active.audio_path && (
                    <div data-testid="feedback-voice-note">
                      <p className="label-overline mb-1.5">Voice note</p>
                      <MediaViewer type="voice" path={active.audio_path} />
                    </div>
                  )}
                  {active.text_note && (
                    <div>
                      <p className="label-overline mb-1">Note</p>
                      <p className="text-sm bg-sand border-2 border-ink p-3 rounded-2xl whitespace-pre-wrap font-600">{active.text_note}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><p className="label-overline mb-1">From</p><p className="font-700">{active.submitter_name || "Anonymous"}</p></div>
                    <div><p className="label-overline mb-1">Contact</p><p className="truncate font-700">{active.submitter_email || "—"}</p></div>
                  </div>
                  {active.page_url && <div><p className="label-overline mb-1">Page</p><p className="text-sm font-mono break-all">{active.page_url}</p></div>}

                  {active.env_metadata && Object.keys(active.env_metadata).length > 0 && (
                    <div data-testid="feedback-env">
                      <p className="label-overline mb-1.5 flex items-center gap-1.5"><Monitor className="h-3.5 w-3.5" /> Environment</p>
                      <div className="grid grid-cols-2 gap-2 text-sm bg-sand border-2 border-ink rounded-2xl p-3">
                        {[
                          ["OS", active.env_metadata.os],
                          ["Browser", active.env_metadata.browser],
                          ["Viewport", active.env_metadata.viewport],
                          ["Screen", active.env_metadata.screen],
                          ["DPR", active.env_metadata.dpr],
                          ["Language", active.env_metadata.language],
                          ["Timezone", active.env_metadata.tz],
                          ["Online", active.env_metadata.online === false ? "Offline" : "Online"],
                        ].filter(([, v]) => v !== undefined && v !== "" && v !== null).map(([k, v]) => (
                          <div key={k} className="min-w-0"><span className="text-ink/50 font-700">{k}: </span><span className="font-700 break-all">{String(v)}</span></div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(active.console_logs?.length > 0 || active.network_logs?.length > 0) && (
                    <details className="border-2 border-ink rounded-2xl overflow-hidden" data-testid="feedback-logs">
                      <summary className="cursor-pointer select-none px-3 py-2.5 bg-ink text-cream font-800 text-sm flex items-center gap-2">
                        <Terminal className="h-4 w-4" /> Console &amp; network logs
                        <span className="ml-auto text-xs font-700 opacity-70">{(active.console_logs?.length || 0) + (active.network_logs?.length || 0)}</span>
                      </summary>
                      <div className="bg-[#1A1A1A] text-cream/90 p-3 space-y-1.5 max-h-72 overflow-y-auto font-mono text-xs">
                        {(active.console_logs || []).map((l, i) => (
                          <div key={`c${i}`} className="break-all leading-relaxed" data-testid={`console-log-${i}`}>
                            <span className={l.level === "error" ? "text-orange font-800" : "text-sunny font-800"}>[{l.level}]</span>{" "}
                            <span>{l.message}</span>
                          </div>
                        ))}
                        {(active.network_logs || []).map((n, i) => (
                          <div key={`n${i}`} className="break-all leading-relaxed text-teal" data-testid={`network-log-${i}`}>
                            <span className="text-danger font-800">[net {n.status || "ERR"}]</span>{" "}
                            <span className="font-800">{n.method}</span> {n.url}{n.error ? ` — ${n.error}` : ""} <span className="opacity-60">({n.duration_ms}ms)</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {active.has_replay && (
                    <div data-testid="feedback-replay">
                      <p className="label-overline mb-1.5 flex items-center gap-1.5"><PlayCircle className="h-3.5 w-3.5" /> Session replay
                        {(() => { const d = getDevice(active); return (
                          <span className="inline-flex items-center gap-1 text-[10px] font-800 normal-case px-2 py-0.5 rounded-full border-2 border-ink bg-white text-ink/70" data-testid="replay-device"><d.Icon className="h-3 w-3" /> {d.label}</span>
                        ); })()}
                      </p>
                      {replayEvents ? (
                        replayEvents.length > 1
                          ? <SessionReplay events={replayEvents} />
                          : <p className="text-sm text-ink/50 font-600">This replay has expired or is empty.</p>
                      ) : (
                        <button onClick={() => loadReplay(active)} disabled={replayLoading} data-testid="load-replay-btn"
                          className="inline-flex items-center gap-2 text-sm font-800 px-4 py-2.5 rounded-xl border-2 border-ink bg-teal shadow-nb-sm hover:translate-y-[-1px] transition-transform disabled:opacity-60">
                          {replayLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Replay session
                        </button>
                      )}
                    </div>
                  )}
                  <div>
                    <p className="label-overline mb-1.5">Status</p>
                    {canEdit ? (
                      <Select value={active.status} onValueChange={(v) => updateStatus(active, v)}>
                        <SelectTrigger className="border-2 border-ink rounded-xl font-700 bg-white" data-testid="feedback-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="border-2 border-ink rounded-2xl">
                          {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}><span className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full border border-ink ${s.dot}`} />{s.label}</span></SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-sm font-700 px-3 py-1.5 rounded-xl border-2 border-ink bg-white" data-testid="feedback-status-readonly">
                        <span className={`h-2.5 w-2.5 rounded-full border border-ink ${(STATUSES.find((s) => s.v === active.status) || {}).dot}`} />
                        {(STATUSES.find((s) => s.v === active.status) || {}).label || active.status}
                      </span>
                    )}
                  </div>

                  <div>
                    <p className="label-overline mb-1.5">Tags</p>
                    <div className="flex flex-wrap items-center gap-2" data-testid="feedback-tags">
                      {(active.tags || []).map((t) => (
                        <span key={t} className={`inline-flex items-center gap-1 text-xs font-800 px-2.5 py-1 rounded-full border-2 border-ink ${tagColor(t)}`} data-testid={`feedback-tag-${t}`}>
                          {t}
                          {canEdit && <button onClick={() => removeTag(active, t)} data-testid={`remove-tag-${t}`} className="hover:opacity-70"><X className="h-3 w-3" /></button>}
                        </span>
                      ))}
                      {!canEdit && (active.tags || []).length === 0 && <span className="text-xs text-ink/40 font-600">No tags</span>}
                      {canEdit && (
                        <input
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(active, tagInput); } }}
                          data-testid="add-tag-input"
                          placeholder="add tag + Enter"
                          className="text-xs font-700 px-3 py-1.5 rounded-full border-2 border-ink/30 bg-white focus:border-ink outline-none w-32"
                        />
                      )}
                    </div>
                  </div>

                  {/* Jira */}
                  {active.jira_issue_key ? (
                    <a href={active.jira_issue_url} target="_blank" rel="noreferrer" data-testid="jira-issue-link"
                      className="flex items-center justify-between bg-grass/15 border-2 border-grass rounded-2xl px-4 py-3 hover:bg-grass/25 transition-colors">
                      <span className="flex items-center gap-2 text-sm font-700"><SquareKanban className="h-4 w-4 text-grass" /> In Jira · <span className="font-mono">{active.jira_issue_key}</span></span>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  ) : canEdit && jira?.connected ? (
                    <button onClick={() => pushToJira(active)} disabled={pushing} data-testid="send-to-jira-button"
                      className="nb-btn bg-teal w-full py-2.5 text-sm disabled:opacity-60">
                      {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><SquareKanban className="h-4 w-4" /> Send to Jira</>}
                    </button>
                  ) : null}

                  <FeedbackTimeline feedbackId={active.id} canEdit={canEdit} />

                  {canEdit && (
                    <div className="flex items-center justify-between gap-2 pt-1">
                      {active.archived ? (
                        <button onClick={() => archiveFeedback(active, false)} data-testid="unarchive-feedback-button"
                          className="flex items-center gap-2 text-sm text-ink font-700 hover:text-teal"><ArchiveRestore className="h-4 w-4" /> Restore to inbox</button>
                      ) : (
                        <button onClick={() => archiveFeedback(active, true)} data-testid="archive-feedback-button"
                          className="flex items-center gap-2 text-sm text-ink/70 font-700 hover:text-ink"><Archive className="h-4 w-4" /> Archive</button>
                      )}
                      <button onClick={() => setConfirmDel(active)} data-testid="delete-feedback-button"
                        className="flex items-center gap-2 text-sm text-danger font-700 hover:underline"><Trash2 className="h-4 w-4" /> Delete</button>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent className="border-2 border-ink rounded-2xl" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete this feedback?</AlertDialogTitle>
            <AlertDialogDescription className="font-600">
              This permanently removes the item and any attached media, recording and session replay. This can't be undone — use Archive if you just want it out of your inbox.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-2 border-ink rounded-xl font-700" data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button"
              className="bg-danger text-white border-2 border-ink rounded-xl font-700 hover:bg-danger/90"
              onClick={() => { const t = confirmDel; setConfirmDel(null); if (t) deleteFeedback(t); }}>
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
